import { useState } from 'react';
import { Loader2, SearchCode } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function yn(v) {
  return v ? 'yes' : 'no';
}

function VerdictBadge({ pair }) {
  const matched = pair.verdict.would_dedup_match;
  return (
    <Badge variant={matched ? 'outline' : 'destructive'}>
      {matched ? `would match (${pair.verdict.match_rule_if_would_match})` : 'unresolved'}
    </Badge>
  );
}

function reasonList(pair) {
  const v = pair.verdict;
  return [
    ...v.channel_ref.fail_reasons,
    ...(v.canonical_ref ? v.canonical_ref.fail_reasons : []),
    ...v.listing_map.fail_reasons,
    ...v.guest_name.fail_reasons,
    ...v.dates.fail_reasons,
  ];
}

export function HostawayDedupPairProbePanel() {
  const [hostawayProperty, setHostawayProperty] = useState('');
  const [smoobuProperty, setSmoobuProperty] = useState('');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const canRun = hostawayProperty.trim() && smoobuProperty.trim() && month.trim();

  const run = async () => {
    if (!canRun) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.hostawayDedupPairProbe({
        hostaway_property: hostawayProperty.trim(),
        smoobu_property: smoobuProperty.trim(),
        month: month.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const pairs = (r && r.pairs) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <SearchCode className="h-5 w-5 text-blue-600" />
          Hostaway/Smoobu Dedup Pair Probe (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For one Hostaway property, one Smoobu property, and a calendar month, lists every
          calendar-overlapping reservation pair and explains which dedup stage (channel-ref,
          listing-map, guest-name, exact-dates) failed and why. Read-only, no writes, no matcher
          changes. Each property field accepts a name or a raw id copied from another diagnostics
          card (e.g. the Migration Audit leakage table).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Hostaway property name or id
            </label>
            <Input
              value={hostawayProperty}
              onChange={(e) => setHostawayProperty(e.target.value)}
              placeholder="e.g. Castle Street, 4 Bed or 2494758"
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Smoobu property name or id
            </label>
            <Input
              value={smoobuProperty}
              onChange={(e) => setSmoobuProperty(e.target.value)}
              placeholder="e.g. Super Spacious 4 Bed, sleeps up to 6 or 2494758"
            />
          </div>
          <div className="w-36">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Month
            </label>
            <Input
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="YYYY-MM"
              className="font-mono"
            />
          </div>
          <Button onClick={run} disabled={loading || !canRun} data-testid="dedup-pair-probe-run">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <SearchCode className="h-4 w-4 mr-2" />
            )}
            Probe
          </Button>
        </div>

        {r && r.error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2">
            <p className="font-medium">{r.error}</p>
            {(r.candidates || []).length > 0 && (
              <div>
                <p className="text-xs font-medium">Candidates:</p>
                <ul className="text-xs list-disc list-inside">
                  {r.candidates.map((c, i) => (
                    <li key={i}>{c.name} ({c.property_id})</li>
                  ))}
                </ul>
              </div>
            )}
            {(r.all_hostaway_properties || r.all_smoobu_properties) && (
              <div>
                <p className="text-xs font-medium">Available properties:</p>
                <ul className="text-xs list-disc list-inside max-h-48 overflow-y-auto">
                  {(r.all_hostaway_properties || r.all_smoobu_properties).map((c, i) => (
                    <li key={i}>{c.name} ({c.property_id})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {r && !r.error && (
          <div className="space-y-3" data-testid="dedup-pair-probe-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>{r.hostaway_property.name} &harr; {r.smoobu_property.name}</span>
              <span>|</span>
              <span>{r.month}</span>
              <span>|</span>
              <Badge variant={r.listing_map_links_these_properties ? 'outline' : 'destructive'}>
                listing map: {r.listing_map_links_these_properties ? 'linked' : 'not linked'}
              </Badge>
              {!r.hostaway_property.owned && (
                <Badge variant="destructive" title="The live matcher never considers reservations on a Hostaway listing that isn't marked owned -- this is why actual_live_matcher_result may say unmatched even when would_dedup_match is true below.">
                  Hostaway listing not marked owned
                </Badge>
              )}
              <Badge variant={r.pair_count > 0 ? 'destructive' : 'outline'}>
                {r.pair_count} overlapping pair(s)
              </Badge>
              {r.unpaired_hostaway_count > 0 && (
                <Badge variant="outline">{r.unpaired_hostaway_count} unpaired Hostaway</Badge>
              )}
              {r.unpaired_smoobu_count > 0 && (
                <Badge variant="outline">{r.unpaired_smoobu_count} unpaired Smoobu</Badge>
              )}
            </div>

            {Object.keys(r.unresolved_fail_reason_counts || {}).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(r.unresolved_fail_reason_counts).map(([reason, count]) => (
                  <Badge key={reason} variant="outline" className="text-amber-600">
                    {reason}: {count}
                  </Badge>
                ))}
              </div>
            )}

            {pairs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No calendar-overlapping pairs found for this property pair and month.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Ids (hw / sm)</th>
                      <th className="text-left p-2">Dates (hw / sm)</th>
                      <th className="text-left p-2">Guest (hw / sm)</th>
                      <th className="text-left p-2">Channel ref (hw / sm)</th>
                      <th className="text-right p-2">Total price (hw / sm)</th>
                      <th className="text-center p-2">Sm superseded / Hw excluded</th>
                      <th className="text-left p-2">Verdict</th>
                      <th className="text-left p-2">Fail reasons</th>
                      <th className="text-left p-2">Live matcher</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.map((p, i) => (
                      <tr key={i} className="border-t align-top" data-testid="dedup-pair-probe-row">
                        <td className="p-2 font-mono">{p.hostaway.id} / {p.smoobu.id}</td>
                        <td className="p-2 font-mono">
                          {p.hostaway.check_in}&rarr;{p.hostaway.check_out}
                          {' / '}
                          {p.smoobu.check_in}&rarr;{p.smoobu.check_out}
                          {!p.verdict.dates.exact_match && (
                            <div className="text-amber-600">not exact</div>
                          )}
                        </td>
                        <td className="p-2">
                          {p.hostaway.guest_name || <em>(empty)</em>} / {p.smoobu.guest_name || <em>(empty)</em>}
                        </td>
                        <td className="p-2 font-mono">
                          {p.hostaway.channel_reservation_id_raw || '—'} / {p.smoobu.channel_reservation_id_raw || '—'}
                          {p.verdict.match_rule_if_would_match === 'canonical_confirmation_code' && (
                            <div className="text-emerald-600">
                              canonical: {p.hostaway.channel_reservation_id_canonical}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {p.hostaway.total_price ?? '—'} / {p.smoobu.total_price ?? '—'}
                        </td>
                        <td className="p-2 text-center">
                          {yn(p.smoobu.superseded_by)} / {yn(p.hostaway.excluded_reason)}
                        </td>
                        <td className="p-2"><VerdictBadge pair={p} /></td>
                        <td className="p-2">
                          {reasonList(p).length === 0 ? '—' : reasonList(p).join(', ')}
                        </td>
                        <td className="p-2 font-mono">{p.verdict.actual_live_matcher_result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
